import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { materialHub } from "../mock";

/**
 * Interactive Material Hub
 * Central Ruken logo hub with 4 material wings (Gypsum, Clinker, Limestone, Slag).
 * Vector flow lines connect each material to the center; on hover, the connecting
 * line lights up in brand green + a light pulse moves toward the logo.
 */
export default function MaterialHub() {
  const [hovered, setHovered] = useState(null);

  // Card positions (SVG-relative center points, viewBox 1000x600)
  const positions = {
    tl: { x: 200, y: 150 },
    tr: { x: 800, y: 150 },
    bl: { x: 200, y: 450 },
    br: { x: 800, y: 450 },
  };
  const center = { x: 500, y: 300 };

  return (
    <section className="section-padding" style={{ background: "linear-gradient(180deg, #ffffff 0%, var(--brand-50) 100%)" }}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center max-w-3xl mx-auto mb-12 fade-in">
          <p className="text-xs uppercase tracking-[0.3em] font-semibold" style={{ color: "var(--brand-500)" }}>
            Interactive Material Hub
          </p>
          <h2 className="font-display text-3xl md:text-5xl font-bold mt-2" style={{ color: "var(--brand-700)" }}>
            Our Product at a Glance
          </h2>
          <p className="mt-4 text-muted-foreground">
            Explore how our four core bulk commodities flow through Ruken's global trading network.
          </p>
        </div>

        <div className="relative fade-in">
          {/* Connecting flow lines (SVG) */}
          <svg
            className="absolute inset-0 w-full h-full pointer-events-none hidden md:block"
            viewBox="0 0 1000 600"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <defs>
              <linearGradient id="lineActive" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#40916c" stopOpacity="0.2" />
                <stop offset="50%" stopColor="#40916c" stopOpacity="1" />
                <stop offset="100%" stopColor="#40916c" stopOpacity="0.2" />
              </linearGradient>
            </defs>
            {Object.entries(positions).map(([pos, p]) => {
              const active = hovered === pos;
              return (
                <g key={pos}>
                  <line
                    x1={p.x}
                    y1={p.y}
                    x2={center.x}
                    y2={center.y}
                    stroke={active ? "url(#lineActive)" : "#d8f3dc"}
                    strokeWidth={active ? 3 : 1.5}
                    strokeDasharray={active ? "0" : "6 6"}
                    style={{ transition: "all 0.4s ease" }}
                  />
                  {active && (
                    <circle r="6" fill="#40916c" opacity="0.9">
                      <animateMotion
                        dur="1.2s"
                        repeatCount="indefinite"
                        path={`M ${p.x} ${p.y} L ${center.x} ${center.y}`}
                      />
                    </circle>
                  )}
                </g>
              );
            })}
          </svg>

          {/* Grid layout for cards + central logo */}
          <div className="relative grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-10 items-center min-h-[560px]">
            {/* Top-Left */}
            <MaterialCard
              item={materialHub[0]}
              onHover={(v) => setHovered(v ? "tl" : null)}
              className="md:col-start-1 md:row-start-1"
            />
            {/* Top-Right */}
            <MaterialCard
              item={materialHub[1]}
              onHover={(v) => setHovered(v ? "tr" : null)}
              className="md:col-start-3 md:row-start-1"
            />

            {/* Center hub */}
            <div className="md:col-start-2 md:row-start-1 md:row-span-2 flex items-center justify-center relative z-10">
              <div className="relative">
                <div className="absolute -inset-6 rounded-full blur-2xl opacity-40" style={{ background: "var(--brand-300)" }} />
                <div className="relative w-40 h-40 md:w-52 md:h-52 rounded-full bg-white shadow-xl border-4 flex items-center justify-center p-6" style={{ borderColor: "var(--brand-500)" }}>
                  <img src="/logo.png" alt="Ruken Alazdehar" className="w-full h-auto object-contain" />
                </div>
              </div>
            </div>

            {/* Bottom-Left */}
            <MaterialCard
              item={materialHub[2]}
              onHover={(v) => setHovered(v ? "bl" : null)}
              className="md:col-start-1 md:row-start-2"
            />
            {/* Bottom-Right */}
            <MaterialCard
              item={materialHub[3]}
              onHover={(v) => setHovered(v ? "br" : null)}
              className="md:col-start-3 md:row-start-2"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function MaterialCard({ item, onHover, className = "" }) {
  return (
    <Link
      to={item.to}
      onMouseEnter={() => onHover(true)}
      onMouseLeave={() => onHover(false)}
      className={`group relative rounded-2xl overflow-hidden border border-border bg-white shadow-sm hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 z-10 ${className}`}
    >
      <div className="aspect-[16/10] overflow-hidden">
        <img src={item.image} alt={item.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
      </div>
      <div className="p-5">
        <h3 className="font-display font-bold text-lg" style={{ color: "var(--brand-700)" }}>{item.name}</h3>
        <span className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider group-hover:gap-3 transition-all" style={{ color: "var(--brand-500)" }}>
          Explore <ArrowRight size={13} />
        </span>
      </div>
    </Link>
  );
}
